import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Surface, ToggleChip } from "@vd/ui";
import { useState } from "react";
import { accountKeys, type InvoiceFilter, invoicesQuery } from "../../../api/billingAccount";
import { billingSummaryQuery } from "../../../api/billingPlan";
import { requireAdmin } from "../../../auth/useMe";
import { AddPaymentMethodDialog } from "../../../components/billing/account/AddPaymentMethodDialog";
import { InvoiceTable } from "../../../components/billing/account/InvoiceTable";
import { usePayInvoice } from "../../../components/billing/account/usePayInvoice";
import { Button, ErrorState, Loading } from "../../../components/ui";

// Admin > Billing > Invoices (DESIGN.md "Billing (console)", Invoices tab, story P21): newest
// first, 25 a page with Load more, filter chips All, Paid and Unpaid (the server filters, so a
// filtered list pages through every match). The PDF and receipt open Stripe's documents in a new
// tab through the server's redirect. Pay now pays a Failed or Open invoice with the default
// method (spec D20); with no method it opens Add payment method.

export const Route = createFileRoute("/admin/billing/invoices")({
  staticData: { crumb: { label: "Invoices", parents: [] } },
  beforeLoad: requireAdmin,
  loader: ({ context }) => context.queryClient.prefetchInfiniteQuery(invoicesQuery("all")),
  component: InvoicesTab,
});

const FILTERS: { value: InvoiceFilter; label: string; empty: string }[] = [
  { value: "all", label: "All", empty: "No invoices yet" },
  { value: "paid", label: "Paid", empty: "No paid invoices" },
  { value: "unpaid", label: "Unpaid", empty: "No unpaid invoices" },
];

function InvoicesTab() {
  const [filter, setFilter] = useState<InvoiceFilter>("all");
  // The last list stays on screen while the next chip's loads, so nothing jumps.
  const q = useInfiniteQuery({ ...invoicesQuery(filter), placeholderData: keepPreviousData });
  const shown = q.data?.pages.flatMap((p) => p.invoices) ?? [];
  const empty = FILTERS.find((f) => f.value === filter)?.empty;
  const qc = useQueryClient();
  const summary = useQuery(billingSummaryQuery);
  const [adding, setAdding] = useState(false);
  const pay = usePayInvoice(() => setAdding(true));

  return (
    <div className="flex max-w-[880px] flex-col gap-5">
      <fieldset className="flex gap-1.5">
        <legend className="sr-only">Show</legend>
        {FILTERS.map((f) => (
          <ToggleChip
            key={f.value}
            pressed={filter === f.value}
            onPressedChange={() => setFilter(f.value)}
            className={filter === f.value ? "bg-contrast text-on-contrast" : undefined}
          >
            {f.label}
          </ToggleChip>
        ))}
      </fieldset>
      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <>
          <Surface radius="xl" className="px-1 pt-1 pb-1.5">
            {shown.length === 0 ? (
              <p className="flex min-h-14 items-center px-3 text-ink-3 text-ui">{empty}</p>
            ) : (
              <InvoiceTable
                invoices={shown}
                onPay={(inv) => pay.mutate(inv.id)}
                paying={pay.isPending ? (pay.variables ?? null) : null}
              />
            )}
          </Surface>
          {q.hasNextPage ? (
            <Button
              size="sm"
              className="self-center"
              busy={q.isFetchingNextPage}
              onClick={() => void q.fetchNextPage()}
            >
              Load more
            </Button>
          ) : null}
        </>
      )}
      <AddPaymentMethodDialog
        open={adding}
        onClose={() => setAdding(false)}
        onAdded={() => void qc.invalidateQueries({ queryKey: accountKeys.methods })}
        interval={summary.data?.interval ?? null}
        hasDefault={false}
      />
    </div>
  );
}
